/**
 * @description 날짜 데이터를 YYYYMMDD 형식으로 변경
 * @author CHOISEONHA
 * @date 04/16/2024
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FxWindow } from "../../fxms/windows/FxWindows";

export const formatDateFromBigInt = (bigIntDate) => {
  if (bigIntDate && bigIntDate.length >= 0) {
    const year = bigIntDate.substring(0, 4);
    const month = bigIntDate.substring(4, 6);
    const day = bigIntDate.substring(6, 8);
    return `${year}-${month}-${day}`;
  } else {
    return "NaN";
  }
}

/**
 * @description 날짜 데이터를 HH:MM ~ (15분 뒤의)HH:MM 형식으로 변경
 * @author CHOISEONHA
 * @date 04/16/2024
 */
export const formatTimeRange1 = (datetimeStr) => {
  if (datetimeStr && datetimeStr.length === 14) {
    let hours = parseInt(datetimeStr.substring(8, 10), 10);
    let minutes = parseInt(datetimeStr.substring(10, 12), 10);

    let time = new Date();
    time.setHours(hours, minutes, 0, 0);
    let startTime = `${padWithZero(time.getHours())}:${padWithZero(time.getMinutes())}`;

    if (startTime === '23:45') {
      let endTime = '23:59';  // 하루의 끝을 23:59로 처리하여 00:00 중복 방지
      return `${startTime} ~ ${endTime}`;
    } else {
      time.setMinutes(time.getMinutes() + 15);
      let endTime = `${padWithZero(time.getHours())}:${padWithZero(time.getMinutes())}`;
      return `${startTime} ~ ${endTime}`;
    }
  } else {
    return "Invalid Format";
  }
}


export const formatTimeRange2 = (datetimeStr) => {
  if (datetimeStr && datetimeStr.length === 14) {
    let year = parseInt(datetimeStr.substring(0, 4), 10);
    let month = parseInt(datetimeStr.substring(4, 6), 10);
    let day = parseInt(datetimeStr.substring(6, 8), 10);
    let hours = parseInt(datetimeStr.substring(8, 10), 10);
    let minutes = parseInt(datetimeStr.substring(10, 12), 10);

    let time = new Date(year, month - 1, day, hours, minutes, 0, 0);
    let formattedDate = `${year}-${padWithZero(month)}-${padWithZero(day)}`;
    let startTime = `${padWithZero(time.getHours())}:${padWithZero(time.getMinutes())}`;

    if (startTime === '23:45') {
      let endTime = '00:00';
      time.setDate(time.getDate() + 1);
      let nextDay = `${time.getFullYear()}-${padWithZero(time.getMonth() + 1)}-${padWithZero(time.getDate())}`;
      return `${formattedDate} ${startTime} ~ ${nextDay} ${endTime}`;
    } else {
      time.setMinutes(time.getMinutes() + 15);
      let endTime = `${padWithZero(time.getHours())}:${padWithZero(time.getMinutes())}`;
      return `${formattedDate} ${startTime} ~ ${formattedDate} ${endTime}`;
    }
  } else {
    return "Invalid Format";
  }
}

const padWithZero = (num) => {
  return num.toString().padStart(2, '0');
}


/**
 * @description 오늘 날짜 데이터를 YYYYMMDD 형식으로 변경
 * @author CHOISEONHA
 * @date 04/17/2024
 */
export const getCurrentDateYYYYMMDD = () => {
  const today = new Date();
  const year = today.getFullYear();
  let month = today.getMonth() + 1;
  let day = today.getDate();

  // 월과 일이 10보다 작으면 앞에 '0'을 붙여서 두 자리 수로 만듬
  month = month < 10 ? '0' + month : month;
  day = day < 10 ? '0' + day : day;

  return `${year}${month}${day}`;
};

/**
 * @description 7일 전의 날짜를 YYYYMMDD 형식으로 리턴
 * @author CHOISEONHA
 * @date 04/24/2024
 */
export const SevenDaysBefore = () => {
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - 7);
  const formattedDate = currentDate.toISOString().slice(0, 10).replace(/-/g, '');
  return formattedDate;
};

/**
 * @description 오늘 날짜 데이터를 YYYYMMDD000000 형식으로 리턴
 * @author CHOISEONHA
 * @date 04/24/2024
 */
export const StartOfDay = () => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);  // 밀리초도 최대값으로 설정
  const formattedDate = currentDate.toISOString().replace(/-|:|T|Z|\.\d{3}/g, '').slice(0, 14);
  return formattedDate.slice(0, 8) + "000000";
};

/**
 * @description  오늘 날짜의 23시 59분 59초를 YYYYMMDD235959로 리턴
 * @author CHOISEONHA
 * @date 04/24/2024
 */
export const EndOfDay = () => {
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);  // 밀리초도 최대값으로 설정
  const formattedDate = currentDate.toISOString().replace(/-|:|T|Z|\.\d{3}/g, '').slice(0, 14);
  return formattedDate.slice(0, 8) + "235959";
};

export const getDaysBetween = (d1, d2) => {
  const date1 = new Date(
    d1?.substr(0, 4),
    d1?.substr(4, 2) - 1,
    d1?.substr(6, 2)
  );
  const date2 = new Date(
    d2?.substr(0, 4),
    d2?.substr(4, 2) - 1,
    d2?.substr(6, 2)
  );
  const timeDiff = date2.getTime() - date1.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

/**
 * @description  오늘 날짜를 Tue Feb 04 2025 22:33:55 GMT+0900 (한국 표준시) 형식으로 리턴
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getTodayISO = () => {
  const offset = new Date().getTimezoneOffset() * 60000;
  const today = new Date(Date.now() - offset);
  return today;
}

/**
 * @description  달 차이를 리턴 입력 d1: yyyymmdd d2: yyyymmdd  
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getMonthBetween = (d1, d2) => {
  const year1 = parseInt(d1?.substr(0, 4));
  const month1 = parseInt(d1?.substr(4, 2));
  const year2 = parseInt(d2?.substr(0, 4));
  const month2 = parseInt(d2?.substr(4, 2));

  const monthDiff = (year2 - year1) * 12 + (month2 - month1);
  console.log("monthDiff >>>", monthDiff);

  return monthDiff;
};

/**
 * @description  일자를 입력하여 원하는 달 계산 ex) 3개월전 , month -3
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getMonthByDate = (month) => {
  const today = getTodayISO();
  const result = new Date(today.setMonth(today.getMonth() + (month)) );
  return result.toISOString().slice(0, 10);
};


/**
 * @description  어제날짜를 YYYY-MM-DD로 리턴
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getYesterday = () => {
  const today = new Date();
  const yesterday = new Date(today.setDate(today.getDate() - 1) );
  return toKSTISOString(yesterday);
}

export const getYesterDayOfMonth = () => {
  const today = new Date();
  const yesterday = new Date(today.setDate(today.getDate() - 1) );
  const firstDayOfMonth = new Date(yesterday.getFullYear(), yesterday.getMonth(), 1);
  return toKSTISOString(firstDayOfMonth);
}
/**
 * @description  이번 달의 첫번째 날을 YYYY-MM-DD로 리턴
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getFirstDayOfMonth = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  return toKSTISOString(firstDayOfMonth);
}
/**
 * @description  오늘날짜를 YYYY-MM-DD로 리턴
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getToday = () => {
  return toKSTISOString(new Date());
}


/**
 * @description  오늘을 YYYYMMDDHHMMSS로 리턴
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getNow = () => {
  const today = getTodayISO();
  return today.toISOString().replace(/-|:|T|Z|\.\d{3}/g, '').slice(0, 14);
}
/**
 * @description   오늘 기준으로 원하는 날 구하기 ex) day 가 -3 이면 3일전 YYYY-MM-dd로 리턴 
 * @author SONJIEUN
 * @date 01/02/2025
 */
export const getDay = (day) => {
  const today = getTodayISO();
  today.setDate(today.getDate() + (day));
  return today.toISOString().slice(0, 10);
}

/**
 * @description  이번달 기준으로 원하는 달 구하기 입력 ex) month 가 -3 이면 3달전 YYYY-MM로 리턴 
 * @author SONJIEUN
 * @date 04/24/2024
 */
export const getMonth = (month) => {
  const today = getTodayISO();
  const resultMonth = new Date(today.setMonth(today.getMonth() + (month)) );
  return resultMonth.toISOString().slice(0, 7);
}



/**
 * @description  산단, 회사 정보를 넣어주기 위한 기능
 * @author SONJIEUN
 * @date 11/19/2024
 */
export const getInloInfo = (inlo,user) => {

  const allName = inlo.inloAllName.replaceAll(' ','');
  const inloList = allName.split(">");

  if(user.ugrpNo === 107){              //vup계정
    return inlo;
  }else if(user.ugrpNo === 1021){      //산단계정  
    inlo.complexInloNo = inlo.inloNo;
    inlo.complexInloName = inloList[0];   
  }else if(user.ugrpNo === 1024){     //공장계정
    inlo.complexInloNo = inlo.ownerInloNo;
    inlo.companyInloNo = inlo.upperInloNo;   
    inlo.complexInloName = inloList[0];    
    inlo.companyInloName = inloList[1];   
  }

  
  return inlo;
}

/**
 * @description 오늘의 요일 가져오는 함수
 * @author SONJIEUN
 * @date 02/03/2025
 */
export const getDayOfWeek=()=>{ 
 
  const week = ['일', '월', '화', '수', '목', '금', '토'];

  const dayOfWeek = week[new Date().getDay()];

  return dayOfWeek;

}

/**
 * @description 오늘의 15분 단위로 현재시간 기준 가장 빠른 미래시간을 표시 return : 2025.02.03(월) 14:30
 * @author SONJIEUN
 * @date 02/03/2025
 */
export const get15MinOfDate=()=>{
  let now = getNow(); 

  let nowMin =Number(now.substring(10,12));
  
  let resultMin = nowMin/15 !== 1 ? 15 * ( 1 +  Math.floor(nowMin / 15)  ): nowMin;

  let resulthour  = Number(now.substring(8,10));

  if(resultMin===60){
      resultMin  = '00';
      resulthour = resulthour+1;
  }

  let textDate = now.substring(0,4) + '.'+now.substring(4,6)+'.'+now.substring(6,8) + '(' + getDayOfWeek() +') '
  + resulthour + ":" + resultMin;

  return textDate;
}

// KST(한국 표준시) 기준 ISO 날짜 문자열 반환 (YYYY-MM-DD)
export const toKSTISOString = (date = new Date()) => {
  const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
};

// KST(한국 표준시) 기준 ISO 날짜 문자열 반환 (YYYY-MM-DD HH:MM:SS)
export const toKSTISOStringWithTime = (date = new Date()) => {
  const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 19);
};

export async function exportRowsToExcel({ columns, rows, fileName = "tableData", sheetName = "downloadData", excludeId = true }) {
  if (rows.length === 0) {
    FxWindow.alert("조회된 데이터가 없습니다.");
    return;
  }
  // 1. 컬럼 추출
  let exportColumns = columns;
  if (excludeId) {
    exportColumns = columns.filter(col => col.field !== "id");
  }
  // 2. 헤더명 배열
  const colNameList = exportColumns.map(col => col.headerName);
  // 3. rows를 헤더 순서대로, renderCell → valueGetter → valueFormatter → 원본값 순서로 변환
  const exportRows = rows.map(row =>
    exportColumns.map(col => {
      if (col.renderCell) {
        return col.renderCell({ row, value: row[col.field] });
      } else if (col.valueGetter) {
        return col.valueGetter({ row });
      } else if (col.valueFormatter) {
        return col.valueFormatter({ value: row[col.field], row });
      }
      return row[col.field];
    })
  );
  // 4. 엑셀 시트 생성
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  worksheet.addRow(colNameList);
  exportRows.forEach(row => worksheet.addRow(row));

  const today = getCurrentDateYYYYMMDD();
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${fileName}_${today}.xlsx`);
};